import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Contact } from '../model/contact';
import { HttpClient, HttpParams } from '@angular/common/http';
import { of } from 'rxjs/index';
import { catchError, map, tap } from 'rxjs/internal/operators';
import { ApiRoutes } from 'src/app/config/api-routes';
import { ApplicationConfig } from 'src/app/config/application.config';
import { LoggerService } from 'src/app/service/logger.service';
import { ContactSearchParams } from '../model/contactSearchParams';
import { PaginationResponse } from '../model/paginationResponse';
import { ContactSummary } from '../model/contactSummary';

@Injectable()
export class ContactsService {
  observable: Observable<ContactSummary[]>;
  contacts: ContactSummary[];
  detailsUpdated$ = new Subject<any>();
  functionsUpdated$ = new Subject<any>();
  deleteSubject$ = new Subject<string>();
  added$ = new Subject<Contact>();

  constructor(
    private httpClient: HttpClient,
    private config: ApiRoutes,
    private appConfig: ApplicationConfig,
    private logger: LoggerService
  ) { }

  getContacts(): Observable<ContactSummary[]> {
    if (this.contacts) {
      return of(this.contacts);
    } else {
      if (this.observable == null) {
        this.observable = this.getContactsFromServer();
        this.observable.subscribe(contacts => this.contacts = contacts);
      }
      return this.observable;
    }
  }

  public getContactsFromServer(): Observable<ContactSummary[]> {
    return this.httpClient.get<ContactSummary[]>(this.config.CONTACTS)
      .pipe(
        map(contacts => contacts.map(contact => {
          return contact;
        })),
        catchError(() => {
          return of([]);
        })
      );
  }

  public searchContacts(search: ContactSearchParams): Observable<PaginationResponse<ContactSummary>> {
    return this.httpClient.post<PaginationResponse<ContactSummary>>(this.config.CONTACTS_SEARCH, search)
      .pipe(
        catchError(() => of({ items: [], page: 0, total: 0 } as PaginationResponse<ContactSummary>))
      );
  }

  public getContact(contactId: string): Observable<Contact> {
    return this.httpClient
      .get<Contact>(ApiRoutes.getRouteWithInjectedParams(this.config.CONTACT, [contactId]));
  }

  updateContact(value: Contact): Observable<boolean> {
    const contact: Contact = Object.assign({}, value);
    return this.httpClient.put<boolean>(this.config.CONTACTS, contact)
      .pipe(
        tap(() => this.detailsUpdated$.next(null))
      );
  }

  public createContact(contact: Contact): Observable<Contact> {
    this.setContactOrganizationToNullIfItsEmptyString(contact);
    return this.httpClient.post<Contact>(this.config.CONTACTS, contact).pipe(
      tap((id) => this.added$.next(id)),
      catchError(() => {
        return of({} as Contact);
      })
    );
  }

  private setContactOrganizationToNullIfItsEmptyString(contact: Contact) {
    if (contact.company === '') {
      contact.company = null;
    }
  }

  deleteContact(contactId: string): Observable<boolean> {
    return this.httpClient.delete<boolean>(
      ApiRoutes.getRouteWithInjectedParams(this.config.CONTACT, [contactId]))
      .pipe(
        tap(() => this.deleteSubject$.next(contactId))
      );
  }

  inviteToJobsLogin(contactId: string): Observable<boolean> {
    const url = ApiRoutes.getRouteWithInjectedParams(this.config.CONTACT_INVITE_TO_JOBS, [contactId]);
    return this.httpClient
      .post(url, null)
      .pipe(
        map(() => {
          return true;
        }),
        catchError(() => {
          return of(false);
        })
      );
  }
}
